import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaLightbulb, FaHandshake, FaTrophy } from 'react-icons/fa';
import { Rocket, Code, TrendingUp, Users } from 'lucide-react';
import SpatialShowcase from './ui/spatial-product-showcase';
import { PerspectiveMarquee } from './ui/perspective-marquee';
import { ShaderBackground } from './ui/animated-shader-hero';
import HighlightCard from './ui/highlight-card';
import './About.css';

function asTranslationArray(value) {
  return Array.isArray(value) ? value : [];
}

const About = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [marqueeFontSize, setMarqueeFontSize] = useState(88);

  useEffect(() => {
    const updateSize = () => {
      const w = window.innerWidth;
      if (w < 480) setMarqueeFontSize(44);
      else if (w < 768) setMarqueeFontSize(60);
      else if (w < 1024) setMarqueeFontSize(72);
      else setMarqueeFontSize(88);
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const valueCards = useMemo(
    () => [
      {
        title: t('values.growth.title'),
        description: [t('values.growth.description')],
        icon: <Rocket className="text-white" aria-hidden />,
      },
      {
        title: t('values.custom.title'),
        description: [t('values.custom.description')],
        icon: <Code className="text-white" aria-hidden />,
      },
      {
        title: t('values.results.title'),
        description: [t('values.results.description')],
        icon: <TrendingUp className="text-white" aria-hidden />,
      },
      {
        title: t('values.partnership.title'),
        description: [t('values.partnership.description')],
        icon: <Users className="text-white" aria-hidden />,
      },
    ],
    [t, i18n.language],
  );

  const highlights = useMemo(
    () => [
      {
        icon: <FaLightbulb />,
        title: t('highlights.strategy.title'),
        description: t('highlights.strategy.description'),
      },
      {
        icon: <FaHandshake />,
        title: t('highlights.communication.title'),
        description: t('highlights.communication.description'),
      },
      {
        icon: <FaTrophy />,
        title: t('highlights.results.title'),
        description: t('highlights.results.description'),
      },
    ],
    [t, i18n.language],
  );

  const marqueeItems = useMemo(
    () => asTranslationArray(t('about.marquee.stats', { returnObjects: true })),
    [t, i18n.language],
  );

  return (
    <div className="about">
      {/* Hero — Spatial Showcase (Craft / Code) */}
      <SpatialShowcase />

      {/* Intro / Mission — minimalist */}
      <section className="about-content about-content-top">
        <div className="about-container">
          <motion.div
            className="intro-minimal"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
          >
            <h2 className="intro-headline">{t('about.intro.minimal.headline')}</h2>
            <p className="intro-clever">
              {t('about.intro.minimal.line1')}
              <br className="intro-break" />
              {t('about.intro.minimal.line2')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats — perspective marquee (full-width) */}
      <motion.section
        className="about-marquee-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        aria-label={t('about.aria.statsMarquee')}
      >
        <PerspectiveMarquee
          items={marqueeItems}
          fontSize={marqueeFontSize}
          fontWeight={700}
          color="#ffffff"
          background="#000000"
          fadeColor="#000000"
          pixelsPerFrame={2}
          rotateY={-28}
          rotateX={8}
          perspective={1200}
          speed={1}
        />
      </motion.section>

      {/* What Drives Me — highlight cards (no carousel) */}
      <section className="about-content about-values-highlight">
        <div className="about-container">
          <div className="values-section about-values-cards-wrap">
            <h3>{t('about.values.title')}</h3>
            <div className="about-values-cards-grid">
              {valueCards.map((card) => (
                <div key={card.title} className="about-values-cards-grid__cell">
                  <HighlightCard
                    title={card.title}
                    description={card.description}
                    icon={card.icon}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Highlights / Why choose — animated shader backdrop scene */}
      <section
        className="highlights-scene"
        aria-label={t('about.aria.highlightsScene')}
      >
        <ShaderBackground />
        <div className="highlights-scene__overlay" aria-hidden="true" />
        <div className="highlights-scene__inner">
          <motion.div
            className="highlights-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>{t('about.highlights.title')}</h3>
            <div className="highlights-grid">
              {highlights.map((highlight, index) => (
                <motion.div
                  key={highlight.title}
                  className="highlight-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className="highlight-icon">{highlight.icon}</div>
                  <h4>{highlight.title}</h4>
                  <p>{highlight.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="cta-headline-blackletter">
            {t('about.cta.headline')}
          </h2>
          <p>{t('about.cta.subtitle')}</p>
          <motion.button
            className="cta-button"
            onClick={() => navigate('/start-project')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('about.cta.button')}
            <FaArrowRight className="arrow-icon" />
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default About;
